import { t } from '../../shared/i18n.js';
import { el, clear } from './dom.js';
import { button, card, row, pill, progress, empty, section, trapFocus } from './components.js';
import { Phone } from './phone.js';

function standing(wealth) {
  if (wealth < 0) return t('In debt');
  if (wealth < 150000) return t('Getting by');
  if (wealth < 900000) return t('Comfortable');
  return t('Well off');
}

// Side panels: a townsperson's details, the town's own figures, the news, the player's places,
// the chat, and the phone (drawn by Phone).
export class Panels {
  // ask(type, payload): sends a question to the server.
  constructor(root, ask) {
    this.ask = ask;
    this.node = el('div.panel', { id: 'panel', hidden: true, role: 'dialog', tabindex: '-1' });
    this.phone = new Phone(this);
    this.settings = {};
    this.chatLog = el('div.lines');
    this.chatInput = el('input', { type: 'text', maxlength: 200, placeholder: t('Say something to everyone'), 'aria-label': t('Chat message') });
    this.chatNode = el('div', { id: 'chat' }, this.chatLog, this.chatInput);
    this.chatInput.hidden = true;
    root.append(this.node, this.chatNode);
    this.kind = null;
    trapFocus(this.node);
    // Escape closes from inside a text field too (the game's key handler ignores fields)
    this.node.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.close();
      }
    });
  }

  get open() {
    return !this.node.hidden;
  }

  close() {
    if (this.phone) this.phone.parkPlayer();
    this.node.hidden = true;
    this.kind = null;
    document.body.classList.remove('panel-open');
    const view = document.getElementById('view');
    if (view && this.node.contains(document.activeElement)) view.focus();
  }

  // Clears the panel and gives it a head: title, a line under it, a close button.
  header(title, sub) {
    if (this.phone) this.phone.parkPlayer();
    clear(this.node);
    this.node.hidden = false;
    document.body.classList.add('panel-open');
    this.node.className = 'panel';
    this.node.setAttribute('aria-label', title);
    this.body = el('div.panel-body');
    this.node.append(el('div.panel-head', null, el('div.panel-titles', null, el('h2', { tabindex: '-1' }, title), sub ? el('p.sub', null, sub) : null), button('', { kind: 'ghost', size: 'sm', icon: 'close', ariaLabel: t('Close'), extra: 'close', onclick: () => this.close() })), this.body);
    const h2 = this.node.querySelector('h2');
    requestAnimationFrame(() => h2 && h2.focus({ preventScroll: true }));
  }

  // A person's name that opens their panel (a real button: keyboard and screen readers work).
  personLink(p) {
    if (!p) return t('Unknown');
    const label = p.alive ? p.name : `${p.name} (${t('late')})`;
    return el('button.link', { type: 'button', onclick: () => this.ask('person', { id: p.id }) }, label);
  }

  showPerson(info) {
    if (!info) return;
    this.kind = 'person';
    this.header(info.name, info.alive ? t('{role}, {age} years old', { role: t(info.role), age: info.age }) : t('{role}, died aged {age}', { role: t(info.role), age: info.age }));
    const b = this.body;
    if (info.alive) b.append(el('p.say', null, '“How na?”'));
    b.append(card([
      row({ icon: 'pin', title: t('Lives in'), value: `${info.settlement}, ${info.council}` }),
      row({ icon: 'family', title: t('Family'), value: info.family }),
      row({ icon: 'home', title: t('Household'), value: info.household === 1 ? t('Lives alone') : t('{n} people', { n: info.household }) }),
      row({ icon: 'coin', title: t('Standing'), value: standing(info.wealth) }),
      info.workplace ? row({ icon: 'briefcase', title: t('Works at'), value: info.workplace }) : null,
    ], { padded: false }));
    if (info.knows) b.append(el('p.help', null, info.knows.how === 'neighbour' ? t('Your neighbour in {where}.', { where: info.knows.where }) : info.knows.how === 'family' ? t('Your family.') : t(info.knows.times === 1 ? 'You greeted them {n} time, last at {where} on {date}.' : 'You greeted them {n} times, last at {where} on {date}.', { n: info.knows.times, where: info.knows.where, date: info.knows.last })));
    b.append(section(t('Family')));
    const spouse = info.partner ? this.personLink(info.partner) : info.divorced ? t('Divorced') : info.widowed ? t('Widowed') : t('None');
    b.append(card([
      row({ icon: 'person', title: t('Mother'), value: this.personLink(info.mother) }),
      row({ icon: 'person', title: t('Father'), value: this.personLink(info.father) }),
      row({ icon: 'heart', title: info.sex === 'man' ? t('Wife') : t('Husband'), value: spouse }),
      info.wife2 ? row({ icon: 'heart', title: t('Second wife'), value: this.personLink(info.wife2) }) : null,
      info.fostered ? row({ icon: 'family', title: t('Raised by'), value: t('kin, after losing the parents') }) : null,
    ], { padded: false }));
    if (info.children.length > 0) b.append(section(t('Children ({n})', { n: info.children.length })), el('div.tags', null, info.children.map((c) => this.personLink(c))));
    b.append(section(t('Nature')));
    const nature = card([], { padded: false });
    for (const [label, v] of [[t('Health'), info.health], [t('Mind'), info.mind], [t('Charm'), info.charm]]) {
      const r = row({ title: label, value: `${Math.round((v / 255) * 100)}%` });
      r.querySelector('.row-main').append(progress((v / 255) * 100, { kind: 'accent', label }));
      nature.append(r);
    }
    b.append(nature);
    if (info.mental && info.mental !== 'well') b.append(el('p.help', null, info.mental === 'unwell' ? t('Has not been well in the mind lately; the family carries it.') : t('In need of care; the family looks after them.')));
    if (info.offender) b.append(el('p.help', null, t('Known to the gendarmes.')));
  }

  showTown(m) {
    const tn = m.info;
    this.kind = 'town';
    this.header(tn.name, `${tn.kind === 'village' ? 'Village' : tn.kind === 'town' ? 'Town' : 'Quarter'} in ${tn.council} council`);
    const b = this.body;
    b.append(card([
      row({ icon: 'family', title: t('People'), value: tn.people.toLocaleString('en-US') }),
      row({ icon: 'home', title: t('Homes'), meta: t('{n} empty', { n: tn.emptyHomes }), value: tn.homes.toLocaleString('en-US') }),
      row({ icon: 'build', title: t('Young families housed'), value: String(tn.newHomes) }),
      row({ icon: 'build', title: t('Houses families built'), value: String(tn.built) }),
      row({ icon: 'council', title: t('Fon'), value: tn.fon ? this.personLink({ ...tn.fon, alive: true }) : t('None') }),
      row({ icon: 'council', title: t('Mayor'), value: tn.mayor ? this.personLink({ ...tn.mayor, alive: true }) : t('None') }),
    ], { padded: false }));
    b.append(section(t('Land and animals')));
    const price = tn.foodPrice > 1.4 ? t('dear') : tn.foodPrice < 0.85 ? t('cheap') : t('normal');
    b.append(card([
      row({ icon: 'basket', title: t('Food in the market'), value: price, valueTone: price === t('dear') ? 'danger' : price === t('cheap') ? 'success' : undefined }),
      row({ icon: 'sun', title: t('Grass'), value: `${tn.grass}%` }),
      row({ title: t('Goats'), value: String(tn.goats) }),
      row({ title: t('Fowls'), value: String(tn.fowls) }),
      row({ title: t('Cattle'), value: String(tn.cattle) }),
      row({ title: t('Bush animals'), value: String(tn.bushAnimals) }),
      row({ title: t('Hawks, snakes, civets'), value: String(tn.predators) }),
    ], { padded: false }));
    b.append(section(t('The whole region, year {n}', { n: m.summary.year })));
    b.append(card([
      row({ icon: 'family', title: t('People'), value: m.summary.people.toLocaleString('en-US') }),
      row({ title: t('Births so far'), value: m.summary.births.toLocaleString('en-US') }),
      row({ title: t('Deaths so far'), value: m.summary.deaths.toLocaleString('en-US') }),
      m.summary.left ? row({ title: t('Left for the cities'), value: m.summary.left.toLocaleString('en-US') }) : null,
    ], { padded: false }));
    b.append(section(t('Town news')));
    if (m.log.length === 0) b.append(empty(t('Nothing of note has happened yet.'), 'news'));
    else {
      const news = card([], { padded: false });
      for (let k = m.log.length - 1; k >= 0; k--) news.append(row({ icon: 'news', title: m.log[k].text, meta: t('Year {n}', { n: m.log[k].year }) }));
      b.append(news);
    }
  }

  // Headlines as published, each with its source, and the owner's notices first.
  showNews(live) {
    this.kind = 'news';
    this.header(t('News'), t('Real headlines from Cameroonian news sites'));
    const b = this.body;
    if (live.notices.length > 0) {
      b.append(section(t('Notices from this server')));
      b.append(card(live.notices.map((n) => row({ icon: 'bell', tone: 'accent', title: n.text, meta: new Date(n.time).toLocaleDateString('en-GB') })), { padded: false }));
    }
    b.append(section(t('Headlines')));
    if (live.news.length === 0) {
      b.append(empty(t('No headlines could be fetched just now. They are checked every half hour.'), 'news'));
      return;
    }
    const list = card([], { padded: false });
    for (const n of live.news) {
      const when = n.time ? new Date(n.time).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
      list.append(row({ icon: 'news', title: n.title, meta: when ? `${n.source}, ${when}` : n.source, chevron: !!n.link, onclick: n.link ? () => window.open(n.link, '_blank', 'noopener,noreferrer') : undefined }));
    }
    b.append(list);
  }

  // The player's saved places and those friends have shared with them.
  // data: { mine, shared }. here: the region the player is in. regions: id -> name.
  // show(place): asks to be shown the way.
  showPlaces(data, here, regions, show) {
    this.kind = 'places';
    this.header(t('My places'), t('Only you see these, unless you give someone a place\'s code'));
    const b = this.body;
    const name = el('input', { type: 'text', maxlength: 40, placeholder: t('Name the spot you are standing on'), 'aria-label': t('Name for this place') });
    const save = () => {
      if (name.value.trim()) this.ask('saveplace', { name: name.value.trim() });
    };
    name.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') save();
    });
    b.append(el('div.form', null, name, button(t('Save'), { kind: 'primary', onclick: save })));
    const list = (places, friend) => {
      const box = card([], { padded: false });
      for (const p of places) {
        const where = p.r === here ? '' : ` (in the ${regions[p.r] || p.r})`;
        box.append(row({ icon: 'pin', title: p.n + where, meta: friend ? t('from {who}', { who: p.by }) : t('code to share: {code}', { code: p.code }), action: el('span.row-actions', null, button(t('Way'), { size: 'sm', icon: 'map', onclick: () => show(p) }), button('', { kind: 'ghost', size: 'sm', icon: 'close', ariaLabel: t('Remove'), onclick: () => this.ask('dropplace', { code: p.code }) })) }));
      }
      return box;
    };
    if (data.mine.length === 0) b.append(empty(t('Nothing saved yet. Stand at your house, name it above and press Save.'), 'pin'));
    else b.append(list(data.mine, false));
    b.append(section(t('Friends\' places')));
    const code = el('input', { type: 'text', maxlength: 8, placeholder: t('A friend\'s code'), 'aria-label': t('Code of a friend\'s place') });
    const add = () => {
      if (code.value.trim()) this.ask('addplace', { code: code.value.trim() });
    };
    code.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') add();
    });
    b.append(el('div.form', null, code, button(t('Add'), { onclick: add })));
    if (data.shared.length > 0) b.append(list(data.shared, true));
  }

  // The phone: apps over the player's life. data: { life, needs, money, rent, home, atHome, near (place kinds within reach), hour, weekday, njangi, ... }
  showPhone(app, data) {
    this.kind = 'phone';
    this.phoneApp = app;
    document.body.classList.add('panel-open');
    this.phone.show(app, data);
  }

  addChat(from, text) {
    this.chatLog.append(el('p.chat-line', null, from ? pill(from, 'accent') : null, el('span', null, text)));
    while (this.chatLog.children.length > 7) this.chatLog.firstChild.remove();
    const line = this.chatLog.lastChild;
    setTimeout(() => line.classList.add('old'), 12000);
  }

  // Opens the chat box; send(text) is called when Enter is pressed.
  startChat(send, done) {
    const input = this.chatInput;
    input.hidden = false;
    input.value = '';
    input.focus();
    input.onkeydown = (e) => {
      if (e.key !== 'Enter' && e.key !== 'Escape') return;
      if (e.key === 'Enter' && input.value.trim()) send(input.value.trim());
      input.hidden = true;
      input.blur();
      done();
      e.stopPropagation();
    };
  }
}
